import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { ArrowRightIcon, CalendarDaysIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { Button, ButtonLink, ProgressBar, StatusPill } from '../../interaxDesignSystemComponents'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../constants/colors'
import { SprintTeamMembersCollapsible } from '../TrainingSprints/SprintTeamMembersCollapsible'
import type { SprintTeamDetail } from '../../types/TrainingSprintTypes'

interface ActiveSprintOverviewProps {
  sprints?: Array<{
    status: 'active' | 'upcoming' | 'draft' | 'completed' | 'live'
    title: string
    details: Array<{ label: string; value: string | number }>
    progress?: number
    teamDetails?: SprintTeamDetail[]
    unassignedMemberNames?: string[]
  }>
  onViewAll?: () => void
  onViewDetails?: (sprintIndex: number) => void
  className?: string
}

const sprintCardStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '24px',
  borderRadius: '10px',
  background: INTERAX_COLORS.heritageBlueRadial,
  border: `1px solid ${INTERAX_COLORS.white04}`,
  boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white08}`,
  minHeight: 0
} as const

export const ActiveSprintOverview: React.FC<ActiveSprintOverviewProps> = ({
  sprints = [],
  onViewAll,
  onViewDetails,
  className = ''
}) => {
  const { t } = useTranslation()

  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '16px',
        boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      <Stack spacing={3} sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            component="h2"
            sx={{
              ...INTERAX_TYPOGRAPHY.headline4,
              letterSpacing: '-0.5px',
              color: INTERAX_COLORS.white,
              m: 0
            }}
          >
            {t('facilitator.dashboard.containers.activeSprintOverview.title')}
          </Typography>

          <ButtonLink
            onClick={onViewAll}
            iconPosition="right"
            icon={<ArrowRightIcon style={{ width: 16, height: 16, color: INTERAX_COLORS.sun }} />}
          >
            <Typography
              component="span"
              sx={{
                ...INTERAX_TYPOGRAPHY.buttonMedium,
                color: INTERAX_COLORS.sun
              }}
            >
              {t('facilitator.dashboard.containers.activeSprintOverview.viewAll')}
            </Typography>
          </ButtonLink>
        </Stack>

        {sprints.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3
            }}
          >
            {sprints.map((sprint, index) => (
              <Box
                key={`${sprint.title}-${index}`}
                sx={{
                  flexBasis: { xs: '100%', md: 'calc(50% - 12px)' },
                  flexGrow: 1,
                  minWidth: { xs: '100%', md: '280px' }
                }}
              >
                <Box sx={sprintCardStyles}>
                  <StatusPill status={sprint.status} />

                  <Typography
                    component="h3"
                    sx={{
                      ...INTERAX_TYPOGRAPHY.headline5,
                      color: INTERAX_COLORS.white,
                      letterSpacing: '-0.5px',
                      m: 0
                    }}
                  >
                    {sprint.title}
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      rowGap: 2,
                      columnGap: 2,
                      mt: 1
                    }}
                  >
                    {sprint.details.map((detail) => (
                      <React.Fragment key={detail.label}>
                        <Typography
                          sx={{
                            ...INTERAX_TYPOGRAPHY.body3,
                            color: INTERAX_COLORS.thunderGrey
                          }}
                        >
                          {t(detail.label)}
                        </Typography>
                        <Typography
                          sx={{
                            ...INTERAX_TYPOGRAPHY.body3,
                            color: INTERAX_COLORS.white,
                            textAlign: 'right'
                          }}
                        >
                          {detail.value}
                        </Typography>
                      </React.Fragment>
                    ))}
                  </Box>

                  {sprint.teamDetails?.length || sprint.unassignedMemberNames?.length ? (
                    <SprintTeamMembersCollapsible
                      teamDetails={sprint.teamDetails ?? []}
                      unassignedMemberNames={sprint.unassignedMemberNames}
                    />
                  ) : null}

                  {typeof sprint.progress === 'number' && (
                    <Box sx={{ mt: 2 }}>
                      <ProgressBar value={sprint.progress} color="green" />
                    </Box>
                  )}

                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant="secondary"
                      fullWidth
                      onClick={() => {
                        onViewDetails?.(index)
                      }}
                    >
                      {t('facilitator.dashboard.sprintCard.viewDetails')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Stack spacing={2.5} alignItems="center" textAlign="center" sx={{ py: 4 }}>
            <CalendarDaysIcon style={{ width: 64, height: 64, color: INTERAX_COLORS.sun }} />
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body2,
                color: INTERAX_COLORS.white
              }}
            >
              {t('facilitator.dashboard.containers.activeSprintOverview.noActiveSprints')}
            </Typography>

            <ButtonLink
              onClick={onViewAll}
              iconPosition="right"
              icon={<ArrowRightIcon style={{ width: 16, height: 16, color: INTERAX_COLORS.sun }} />}
            >
              <Typography
                component="span"
                sx={{
                  ...INTERAX_TYPOGRAPHY.buttonMedium,
                  color: INTERAX_COLORS.sun
                }}
              >
                {t('facilitator.dashboard.containers.activeSprintOverview.createFirstSprint')}
              </Typography>
            </ButtonLink>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

export default ActiveSprintOverview
